import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ModalComponent } from '@app/shared/modal/modal.component';
import { RichLinkModalComponent } from '@app/shared/components/rich-link-modal/rich-link-modal.component';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { Editor } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import CharacterCount from '@tiptap/extension-character-count';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { createRichLinkExtension, preprocessRichLinks } from './extensions/rich-link.extension';
import { TranslateService } from '@ngx-translate/core';
import { Globals } from '@app/shared/globals/globals';
import { NotifyUtils } from '@app/shared/utils/notify.utils';
import { EditorMessages } from './locale/editor.messages';
import { CompanyFeatures } from '@app/models/company-features.model';
import { Goal } from '@app/models/goals/goal.model';
import { GoalsAPIService } from '@app/shared/api/goals.api.service';
import { firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
// TIPTAP-TRIAL: Remove the four lines below if we drop Tiptap Cloud AI / Paste Handler.
import { TiptapTokenApiService } from '@app/shared/api/tiptap-token.api.service';
import Ai from '@tiptap-pro/extension-ai';
import PasteHandler from '@tiptap-pro/extension-paste-handler';

@Component({
  selector: 'app-editor-response',
  templateUrl: './editor-response.component.html',
  styleUrls: ['./editor-response.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: EditorResponseComponent,
      multi: true,
    },
  ],
})
export class EditorResponseComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  static readonly MAX_DEFAULT = 10000;
  public readonly eEditorMessages = EditorMessages;
  public readonly eCompanyFeatures = CompanyFeatures;

  @Input() disabled = false;
  @Input() display = false;
  @Input() resize: boolean;
  @Input() height: number | string;
  @Input() maxCharacters = EditorResponseComponent.MAX_DEFAULT;
  @Input() hardMax = EditorResponseComponent.MAX_DEFAULT;
  @Input() inline: boolean;
  @Input() wordcountChanged: EventEmitter<number>;
  @Input() toolbar: string | boolean;
  @Input() toolbarMobile: string | boolean;
  @Input() menubar: string | boolean;
  @Input() menubarMobile: string | boolean;
  @Input() plugins: string;
  @Input() pluginsMobile: string;
  @Input() contextMenu: string;
  @Input() columnIndent: boolean;
  @Input() showErrorBorder: boolean;
  @Input() enforceCharacterLimit: boolean;
  @Input() placeholder: string;
  @Input() autofocus: boolean;
  @Input() id: string;

  @Output() autoEvent = new EventEmitter<boolean>();
  @Output() saveRequest = new EventEmitter<string>();

  @ViewChild('editorContainer') editorContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('richLinkModal') richLinkModal?: ModalComponent;
  @ViewChild('richLinkPicker') richLinkPicker?: RichLinkModalComponent;

  editor: Editor | null = null;
  richLinkModalReady = false;
  aiState: 'idle' | 'loading' | 'done' | 'error' = 'idle';
  showRewriteMenu = false;
  showCustomPromptInput = false;
  customPromptText = '';
  private aiAppend = false;
  // FRONTEND-3X2: a Tiptap AI stream registers a `cleanup()` that calls `editor.chain()`
  // when the stream closes. If the editor is destroyed first, `chain()` dereferences a
  // now-null commandManager and throws. `aiInFlight` tracks whether such a stream is live;
  // when it is, teardown is deferred until the stream settles (see settleAi/ngOnDestroy).
  private aiInFlight = false;
  private destroyPending = false;
  wordcount = 0;
  warnWordcount = false;
  _value = '';
  private settingContent = false;

  onChange = (_: any) => {};
  onTouched = () => {};

  get value(): string {
    return this._value;
  }

  // Type-safe accessor for use in the template (inside *ngIf="editor" blocks)
  get tiptap(): Editor {
    return this.editor!;
  }

  get aiResponse(): string {
    const storage = this.editor?.storage['ai'] as { response?: string } | undefined;
    return storage?.response ?? '';
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.showRewriteMenu) return;
    if (!(event.target as HTMLElement).closest('.rewrite-menu-wrap')) {
      this.showRewriteMenu = false;
      this.cdRef.markForCheck();
    }
  }

  constructor(
    private cdRef: ChangeDetectorRef,
    public globals: Globals,
    private notifyUtils: NotifyUtils,
    private translate: TranslateService,
    private tiptapTokenService: TiptapTokenApiService, // TIPTAP-TRIAL
    @Optional() private goalsAPIService: GoalsAPIService,
  ) {
    this.resize = false;
    this.inline = false;
    this.columnIndent = true;
    this.showErrorBorder = false;
    this.autofocus = false;
    this.enforceCharacterLimit = true;
    this.height = 300;
    this.placeholder = '';
    this.id = 'rich-editor';
    this.wordcountChanged = new EventEmitter<number>();
  }

  ngOnInit(): void {}

  ngAfterViewInit(): void {
    if (this.display) return;
    // Defer past the current CD cycle to avoid NG0100 (ExpressionChangedAfterChecked)
    setTimeout(() => this.initEditor());
  }

  // TIPTAP-TRIAL: token is fetched once at init (v3.8.2 requires a string, not a function).
  // The service caches it for 90 min so repeated editor mounts are cheap.
  private async initEditor(): Promise<void> {
    const aiToken = await this.tiptapTokenService.getToken().catch(() => '');
    this._initEditorWithToken(aiToken);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['disabled'] && this.editor) {
      this.editor.setEditable(!this.disabled);
      this.cdRef.markForCheck();
    }
  }

  ngOnDestroy(): void {
    if (this.editor) {
      this.saveRequest.emit(this.editor.getHTML());
    }
    // FRONTEND-3X2: never tear the editor down while an AI stream is still running — the
    // extension's stream `cleanup()` will call `editor.chain()` once it closes and would
    // crash on a destroyed (null-commandManager) editor. Defer to settleAi(), which fires
    // when the stream reaches onSuccess/onError (the extension always emits a terminal one,
    // including on the fetch error a route change triggers).
    if (this.aiInFlight) {
      this.destroyPending = true;
    } else {
      this.destroyEditorNow();
    }
  }

  private destroyEditorNow(): void {
    this.editor?.destroy();
    this.editor = null;
  }

  // Called when an AI action reaches a terminal state (success or error). If the component
  // was torn down mid-stream, complete the deferred destroy — but on the next macrotask, so
  // the extension's own post-stream cleanup() (which runs right after this callback and
  // touches editor.chain()) executes against the still-live editor first.
  private settleAi(): void {
    this.aiInFlight = false;
    if (this.destroyPending) {
      this.destroyPending = false;
      setTimeout(() => this.destroyEditorNow());
    }
  }

  @HostListener('window:beforeunload')
  handleBeforeUnload(): void {
    if (this.editor) {
      this.saveRequest.emit(this.editor.getHTML());
    }
  }

  private _initEditorWithToken(aiToken: string): void {
    this.editor = new Editor({
      element: this.editorContainer.nativeElement,
      extensions: [
        StarterKit.configure({
          link: { openOnClick: true, HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' } },
        }),
        TextAlign.configure({ types: ['heading', 'paragraph'] }),
        Placeholder.configure({ placeholder: this.placeholder || '' }),
        CharacterCount.configure({ limit: this.maxCharacters }),
        TaskList,
        TaskItem.configure({ nested: true }),
        Table.configure({ resizable: false }),
        TableRow,
        TableHeader,
        TableCell,
        createRichLinkExtension(id => {
          if (!this.goalsAPIService) return Promise.reject(new Error('GoalsAPIService not available'));
          return firstValueFrom(
            this.goalsAPIService.getGoalById(id).pipe(
              map(g => Goal.getGoalCompletionPercentage(g))
            )
          );
        }),
        // TIPTAP-TRIAL: Remove the two extensions below (and their imports) if we drop Tiptap Cloud.
        Ai.configure({
          // appId is unused under Tiptap's Access Control auth: the server authenticates on the
          // token's environment id (the JWT `iss`) and no longer reads the X-App-Id header. It stays
          // only because extension-ai 3.8.2 still types it as required — drop it once the package is
          // upgraded to a version where appId is optional.
          appId: '',
          token: aiToken,
          onLoading: () => {
            // A stream now exists; from here until it settles, teardown must be deferred.
            this.aiInFlight = true;
            this.aiState = 'loading';
            this.cdRef.markForCheck();
          },
          onSuccess: () => {
            this.aiState = 'done';
            this.settleAi();
            this.cdRef.markForCheck();
          },
          onError: async (error: Error) => {
            // The AI service couldn't complete the request — tell the user and leave the
            // loading state, instead of silently spinning on "Generating…" forever.
            this.notifyUtils.notifyWithType('AI writing tools are currently unavailable. Please try again later.', 'danger');
            this.aiState = 'idle';
            this.aiInFlight = false;
            this.cdRef.markForCheck();

            // Component already torn down: honour the deferred destroy and stop — there is
            // no live editor to refresh a token into.
            if (this.destroyPending) {
              this.settleAi();
              return;
            }

            // Auth errors: refresh the token and re-initialise so a later attempt can succeed.
            if (this.isAuthError(error)) {
              const savedContent = this.editor?.getHTML() ?? this._value;
              const freshToken = await this.tiptapTokenService.forceRefresh().catch(() => '');
              // FRONTEND-3X2: defer the destroy/reinit to the next macrotask. The failed
              // stream's cleanup() still runs after this callback and calls editor.chain();
              // destroying the editor synchronously here would null its commandManager and
              // crash that cleanup.
              setTimeout(() => {
                // Torn down while the token was refreshing: just complete teardown.
                if (this.destroyPending) {
                  this.destroyPending = false;
                  this.destroyEditorNow();
                  return;
                }
                this.editor?.destroy();
                this.editor = null;
                this._initEditorWithToken(freshToken);
                if (savedContent) {
                  this.writeValue(savedContent);
                }
              });
            }
          },
        }),
        PasteHandler,
      ],
      content: preprocessRichLinks(this._value || ''),
      editable: !this.disabled,
      autofocus: this.autofocus ? 'end' : false,
      onUpdate: ({ editor }) => {
        if (this.settingContent) return;
        const html = editor.getHTML();
        this._value = html;
        this.onChange(html);
        this.autoEvent.emit(true);
        this.syncCharCount();

        if (this.enforceCharacterLimit && this.wordcount >= this.maxCharacters) {
          this.warnWordcount = true;
          this.notifyUtils.notifyClose();
          this.notifyUtils.notifyWithParms(
            this.translate.instant(EditorMessages.WARN_CHAR_LIMIT_TYPING, { limit: this.maxCharacters }),
            { newest_on_top: true, type: 'warning' }
          );
        }

        this.cdRef.markForCheck();
      },
      onBlur: ({ editor }) => {
        this.onTouched();
        this.saveRequest.emit(editor.getHTML());
      },
      onSelectionUpdate: () => {
        this.cdRef.markForCheck();
      },
      onTransaction: () => {
        this.cdRef.markForCheck();
      },
    });

    this.syncCharCount();
    this.cdRef.markForCheck();
  }

  private isAuthError(error: Error): boolean {
    const msg = error.message?.toLowerCase() ?? '';
    return msg.includes('401') || msg.includes('unauthorized') || msg.includes('forbidden') || msg.includes('403');
  }

  private syncCharCount(): void {
    if (!this.editor) return;
    const count = this.editor.storage.characterCount.characters();
    this.wordcount = count;
    this.warnWordcount = count >= this.maxCharacters;
    this.wordcountChanged.emit(count);
  }

  // ---- AI actions (TIPTAP-TRIAL) ----

  private prepareForAi(): void {
    if (!this.editor) return;
    // Restore editor focus while preserving the existing selection.
    // If nothing is selected (cursor only), select all so the AI has content to act on.
    const { from, to } = this.editor.state.selection;
    if (from === to) {
      this.editor.chain().focus().selectAll().run();
    } else {
      this.editor.commands.focus();
    }
  }

  runAi(intent: 'improve' | 'professional' | 'conversational' | 'concise' | 'constructive' | 'custom'): void {
    this.showRewriteMenu = false;
    if (intent === 'custom') {
      this.showCustomPromptInput = true;
      this.cdRef.markForCheck();
      return;
    }
    this.aiAppend = false;
    this.prepareForAi();
    switch (intent) {
      case 'improve':
        this.editor?.commands.aiRephrase({ insert: false, format: 'rich-text' });
        break;
      case 'professional':
        this.editor?.commands.aiAdjustTone('formal', { insert: false, format: 'rich-text' });
        break;
      case 'conversational':
        this.editor?.commands.aiAdjustTone('conversational', { insert: false, format: 'rich-text' });
        break;
      case 'concise':
        this.editor?.commands.aiShorten({ insert: false, format: 'rich-text' });
        break;
      case 'constructive': {
        const instruction = `Instruction: Rewrite to be more constructive, specific, and actionable.`;
        const textToWorkWith = `Text:\n{context}`;
        const returnInstruction = `Return only the output of the user's prompt.`;
        this.editor?.commands.aiTextPrompt({
          insert: false,
          format: 'rich-text',
          text: `Apply the following instruction to the user's text: ${instruction}.\n\nUser's text: "${textToWorkWith}".\n\n${returnInstruction}`,
        });
        break;
      }
    }
  }

  submitCustomPrompt(): void {
    const prompt = this.customPromptText.trim();
    if (!prompt) return;
    this.showCustomPromptInput = false;
    this.customPromptText = '';
    this.aiAppend = true;
    this.prepareForAi();
    const instruction = `Instruction: ${prompt}`;
    const textToWorkWith = `Text:\n{context}`;
    const returnInstruction = `Return only the new content to append. Do not repeat or include the original text, labels, or any explanation.`;
    this.editor?.commands.aiTextPrompt({
      insert: false,
      format: 'rich-text',
      text: `Apply the following instruction to the text below.\n\n${instruction}\n\n${textToWorkWith}\n\n${returnInstruction}`,
    });
  }

  cancelCustomPrompt(): void {
    this.showCustomPromptInput = false;
    this.customPromptText = '';
    this.cdRef.markForCheck();
  }

  acceptAi(): void {
    if (this.editor) {
      // v3.8.2 rich-text responses are wrapped in <html>/<body> — strip before inserting.
      // Also strip any "Original: ... Rewritten:" preamble the model occasionally emits.
      const storage = this.editor.storage['ai'] as { response: string };
      const cleaned = storage.response
        .replace(/<\/?(?:html|head|body)[^>]*>/gi, '')
        .replace(/^[\s\S]*?(?:rewritten[^:]*:|feedback:)\s*/i, '')
        .trim();

      if (this.aiAppend) {
        // Custom prompt: discard the AI replacement state, then append the response at the end.
        this.editor.commands.aiReject();
        this.editor.commands.focus('end');
        this.editor.commands.insertContent(cleaned);
      } else {
        storage.response = cleaned;
        this.editor.commands.aiAccept();
      }
    }
    this.aiAppend = false;
    this.aiState = 'idle';
    this.cdRef.markForCheck();
  }

  rejectAi(): void {
    this.editor?.commands.aiReject();
    this.aiAppend = false;
    this.aiState = 'idle';
    this.cdRef.markForCheck();
  }

  dismissAiError(): void {
    this.editor?.commands.aiReject();
    this.aiAppend = false;
    this.aiState = 'idle';
    this.cdRef.markForCheck();
  }

  // ---- Goal link modal ----

  openRichLinkModal(): void {
    this.richLinkModalReady = true;
    this.cdRef.markForCheck();
    setTimeout(() => {
      this.richLinkPicker?.refresh();
      this.richLinkModal?.show();
    });
  }

  onGoalsSelected(goals: Goal[]): void {
    this.richLinkModal?.hide();
    goals.forEach(goal => {
      this.editor?.commands.insertContent({ type: 'richLink', attrs: { feature: 'GOALS', id: goal.id } });
    });
    this.editor?.commands.focus();
    this.cdRef.markForCheck();
  }

  writeValue(value: any): void {
    this._value = value ?? '';
    if (this.editor) {
      this.settingContent = true;
      this.editor.commands.setContent(preprocessRichLinks(this._value));
      this.settingContent = false;
      this.syncCharCount();
      this.cdRef.markForCheck();
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.editor?.setEditable(!isDisabled);
    this.cdRef.markForCheck();
  }
}
